import { IDELayout } from '@common/ide/Layout';
import ProjectsList from '@common/ide/ProjectsList';
import { IdeActionProvider, IdeStateProvider, useIDE, useIDEParams } from '@common/ide/state';
import { ReactDndManager } from '@common/react-dnd-manager';
import SearchInput from '@common/SearchInput';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Stack, StackItem } from '@prophecy/ui-v3/Layout';
import { SwitchTabs } from '@prophecy/ui-v3/Tabs';
import React, { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { Provider } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ExecutionServiceProvider } from '../../context/execution';
import { useExecutionServiceInstance } from '../../data/executionHooks';
import { LAST_OBSERVATION_IDE_URL } from '../../utils/localstorage-keys';
import { ConnectorBrowser } from './Components/ConnectorBrowser';
import { Container } from './Container';
import { useDQIDEStore } from './redux/store';

const TabsContainer = styled(StackItem)`
  padding: ${theme.spaces.x12};
  border-right: 1px solid ${theme.colors.gray200};
`;

const StyledStackItem = styled.div`
  border-right: 1px solid ${theme.colors.gray200};
  padding: ${theme.spaces.x12} ${theme.spaces.x16} 0 ${theme.spaces.x12};
`;

function ObservationHomeRoot() {
  const store = useDQIDEStore();
  const { observedEntity } = useIDEParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <Provider store={store}>
      <IDELayout
        leftSlot={
          <Stack height='100%'>
            <StyledStackItem>
              <SearchInput value={searchQuery} onChange={setSearchQuery} data-test-id='observation-home-search-input' />
            </StyledStackItem>
            <TabsContainer grow='1'>
              <SwitchTabs
                onChange={(key) => {
                  navigate(
                    Private_Routes.IDE_Home.getUrl(
                      { entity: Entity.Observation.toLowerCase() },
                      { observedEntity: key }
                    )
                  );
                }}
                defaultActiveKey={observedEntity || Entity.Project}>
                <SwitchTabs.TabPane tab='Projects' key={Entity.Project}>
                  <ProjectsList searchQuery={searchQuery} entity={Entity.Observation} />
                </SwitchTabs.TabPane>
                <SwitchTabs.TabPane tab='Connectors' key={Entity.Connector}>
                  <ConnectorBrowser searchQuery={searchQuery} />
                </SwitchTabs.TabPane>
              </SwitchTabs>
            </TabsContainer>
          </Stack>
        }
        rightSlot={<Container />}
      />
    </Provider>
  );
}

export const ObservationHome = () => {
  const { state, actions, setActions } = useIDE({
    uid: '',
    entity: Entity.Observation,
    projectId: '',
    projectName: '',
    language: BackEndCodeLanguage.scala
  });

  useEffect(() => {
    localStorage.removeItem(LAST_OBSERVATION_IDE_URL);
  }, []);

  const executionService = useExecutionServiceInstance('');

  return (
    <ExecutionServiceProvider value={executionService}>
      <DndProvider manager={ReactDndManager}>
        <IdeStateProvider value={state}>
          <IdeActionProvider value={{ setActions, actions }}>
            <ObservationHomeRoot />
          </IdeActionProvider>
        </IdeStateProvider>
      </DndProvider>
    </ExecutionServiceProvider>
  );
};
